/**
 * SchemaRenderer — top-level component.
 *
 * Takes a FormSchema + react-hook-form context (must be inside a FormProvider)
 * and renders all sections in order.
 *
 * Pre-warms the reference-data cache before rendering so there are no async
 * waterfalls when individual fields load their options.
 */

import { useEffect, useState } from 'react';
import type { FormSchema, RendererProps } from './types';
import { SectionRenderer } from './SectionRenderer';
import { preloadRefData } from './reference-data/loader';
import { collectOptionsRefs } from './utils';

interface Props extends RendererProps {
  schema: FormSchema;
  /** When true render a loading skeleton until ref-data is ready */
  showLoadingState?: boolean;
  /** Forwarded to each SectionRenderer. See RC-3 there. */
  showSectionHeading?: boolean;
}

export function SchemaRenderer({
  schema,
  formId,
  pdfMode = false,
  namePrefix,
  audience,
  showLoadingState = false,
  showSectionHeading = true,
}: Props) {
  const [refDataReady, setRefDataReady] = useState(false);

  // Collect all options_ref paths across the entire schema and pre-warm
  useEffect(() => {
    const allFields = schema.sections.flatMap((s) => [
      ...(s.fields ?? []),
      ...(s.subsections ?? []).flatMap((sub) => sub.fields),
    ]);
    const refs = collectOptionsRefs(allFields);
    if (refs.length === 0) {
      setRefDataReady(true);
      return;
    }
    preloadRefData(refs).then(() => setRefDataReady(true));
  }, [schema]);

  if (showLoadingState && !refDataReady) {
    return (
      <div className="space-y-4 animate-pulse">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-16 bg-slate-100 rounded-lg" />
        ))}
      </div>
    );
  }

  // Sort sections by their order field
  const sortedSections = [...schema.sections].sort((a, b) => a.order - b.order);

  return (
    <div className="space-y-6">
      {sortedSections.map((section) => (
        <SectionRenderer
          key={section.section_id}
          section={section}
          formId={formId}
          pdfMode={pdfMode}
          namePrefix={namePrefix}
          audience={audience}
          showSectionHeading={showSectionHeading}
        />
      ))}
    </div>
  );
}
